import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import { Options } from "@components/InputCst/MultiSelectCst/types";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { WizardStepProps } from "@components/WizardCst/types";
import { AssociationItem, ActivityWizardData } from "../../../types";
import SectionHeader from "./SectionHeader";

const LanguagesSection: React.FC<WizardStepProps<ActivityWizardData>> = ({
  data,
  setData,
  errors,
}) => {
  const { t } = useTranslation("wizard-activity");
  const { id: corporateId = "" } = useParams();

  const { dataDetail: languagesData } = useFetchDataDetail(
    ServicesURL.corporateLanguages.replace(":corporateId", corporateId),
  );

  const options = useMemo<Options[]>(() => {
    if (!languagesData) return [];
    return [
      languagesData.primary_language,
      ...(languagesData.secondary_languages || []),
    ]
      .filter(Boolean)
      .map((lang: { id: string | number; label: string }) => ({
        label: lang.label,
        value: lang.id,
      }));
  }, [languagesData]);

  const value = useMemo<Options[]>(
    () =>
      data.available_languages.map((lang) => {
        const match = options.find(
          (opt) => String(opt.value) === String(lang.id),
        );
        return (
          match ?? { label: lang.label || String(lang.id), value: lang.id }
        );
      }),
    [data.available_languages, options],
  );

  const handleChange = (next: Options[]) => {
    const nextValue: AssociationItem[] = (next || []).map((lang) => ({
      id: lang.value,
      label: String(lang.label),
    }));
    setData({ available_languages: nextValue });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SectionHeader
        title={t("sections.languages.title")}
        description={t("sections.languages.description") || undefined}
      />
      <MultiSelectCst
        id="activity-available-languages"
        label={t("fields.available-languages") || ""}
        value={value}
        onChange={handleChange}
        options={options}
        error={!!errors.available_languages}
        errorMessage={
          errors.available_languages
            ? t(`errors.${errors.available_languages}`) || ""
            : ""
        }
      />
    </Box>
  );
};

export default LanguagesSection;
